import { useState, useEffect } from "react";
import { ScreenContainer } from "../../components";
import { ScreenProps } from "./types";
import { Text, ProgressBar } from "react-native-paper";
import { Platform, View } from "react-native";
import { ScrollableContainer, ActionsContainer, Button } from "../../components";
import { colors } from "../../../theme";
import PdrItem from "../PDRHistoryScreen/components/PdrItem";
import { TouchableOpacity } from "react-native";
import { contentObject } from "./_utils";
import { Slug, SlugList } from "./types";
import { useQuery } from "@apollo/client";
import { PDR_HISTORY_QUERY } from "../PDRHistoryScreen/queries";
import { PdrHistoryQuery } from "../PDRHistoryScreen/PdrHistoryData";
import { sumBy, sortBy } from "lodash";
import { ActivityIndicator } from "react-native-paper";
import { useNavigation } from "@react-navigation/native";
import useAmplitude from "../../hooks/useAmplitude";

const PreviewPDRs = ({
  slug,
  maxCredits,
  setCreditType,
}: {
  slug: Slug;
  maxCredits: number;
  setCreditType: (type: string) => void;
}) => {
  const navigation = useNavigation();
  const { trackEvent } = useAmplitude();
  const content = contentObject[slug];
  const { data, loading } = useQuery<PdrHistoryQuery>(PDR_HISTORY_QUERY);
  const pdrHistory = sortBy(
    data?.currentPerson?.pdrHistory.filter((a) => a.activityCode === SlugList[slug]),
    "submittedAt"
  ).reverse();
  const creditsSum = sumBy(pdrHistory, "creditsAccepted");

  useEffect(() => {
    setCreditType(pdrHistory[0]?.creditType);
  }, [pdrHistory]);

  if (loading) {
    return <ActivityIndicator className="py-32 items-center justify-center mt-6" />;
  }
  const onViewPdrDetail = (id: string) => {
    trackEvent("click-view-pdr-detail", {
      pdr_activity_category_name: slug,
    });
    navigation.navigate("PdrDetails", { id });
  };
  return creditsSum > 0 ? (
    <>
      <View className="border-b border-sky-light my-8 md:mx-6" />
      <View className="mx-6">
        <Text className="text-small-tight font-400 text-ink-lighter">
          {creditsSum} of {maxCredits} PDR credits (for this category) reported in this cycle year
        </Text>
      </View>
      <View className="md:mx-6">
        {pdrHistory?.map((a, key) => (
          <TouchableOpacity onPress={() => onViewPdrDetail(a.id)} key={key}>
            <PdrItem pdr={a} isLast={key === pdrHistory.length - 1} />
          </TouchableOpacity>
        ))}
      </View>
    </>
  ) : (
    <View className="max-w-[390px] mx-auto">
      <Text className="text-regular-tight text-ink-lighter mx-12 text-center mt-14 mb-8">
        {creditsSum} of {content.maxCredits} PDR credits (for this category) reported in this cycle year.
      </Text>
    </View>
  );
};

function PreviewScreen({ navigation, route }: ScreenProps<"PreviewScreen">) {
  const { trackEvent } = useAmplitude();
  const { slug } = route.params as { slug: Slug };
  const content = contentObject[slug];
  const [creditType, setCreditType] = useState("");
  const helperText = [`Credits earned count toward the ${creditType} category`, ...content.list];

  const onContinue = () => {
    navigation.navigate("ActivityFormScreen", { slug: slug });
    trackEvent("click-report-pdr-continue", {
      pdr_activity_category_name: slug,
    });
    if (Platform.OS === "web") {
      // @ts-ignore
      window.scrollTo({ top: 0, behavior: "instant" });
    }
  };

  return (
    <ScreenContainer navigationTitle="Report PDRs">
      {Platform.OS !== "web" && <ProgressBar progress={0.5} color={colors.primary.light} />}

      <ScrollableContainer testID="preview-screen">
        <View className="md:mb-14">
          <View className="mx-6 mt-6">
            <View className="max-w-[420px]">
              <Text className="text-large-normal font-700 text-ink-darkest">Review Previous {content.title} PDR Credits</Text>
            </View>
            <Text className="text-small-normal font-400 text-ink mt-2">{content.description}</Text>
            <View className="mt-3">
              {helperText.map((info, i) => (
                <View key={i} className="flex-row items-start">
                  <Text className="text-small-normal text-ink font-400 mr-2">•</Text>
                  <Text className="text-small-normal text-ink font-400">{info}.</Text>
                </View>
              ))}
              {slug === "golf-coach" && (
                <Text className="text-small-normal text-ink font-400 mt-4">
                  <Text className="font-700">Please note:</Text> Do not report PGA Jr. League, PGA Jr. Golf Camp, or PGA Family
                  Golf activities, as all Player Engagement credits are reported directly by program administrators.
                </Text>
              )}
            </View>
          </View>
          <PreviewPDRs slug={slug} maxCredits={content.maxCredits} setCreditType={setCreditType} />
        </View>
      </ScrollableContainer>
      <ActionsContainer>
        <Button mode="contained" onPress={onContinue}>
          Continue
        </Button>
      </ActionsContainer>
    </ScreenContainer>
  );
}

export default PreviewScreen;
